import { apiInstance } from "@repo/core/services/instances/api";
import { ProposalsResource } from "@repo/core/services/resources/proposals";
import { usePreProposalStore } from "@repo/core/screens/PreProposal/store/preProposalStore";

const proposalsResource = new ProposalsResource(apiInstance);

interface UploadProposalDocumentProps {
  atributos?: Record<string, string>;
  strExtensao: string;
  strTipoDocumento: string;
  strObservacoes: string;
  strBase64: string;
  strNomeArquivo: string;
  datDocumentoExterno: string;
  strVisualizarWEB: string;
  strCliente: string;
  strCpfPortador: string;
  strAutorizacao: string;
  lngProposta: string;
}

interface UploadDocumentFromUrlProps {
  atributos?: Record<string, string>;
  strTipoDocumento: string;
  strObservacoes: string;
  datDocumentoExterno?: string;
  strVisualizarWEB?: string;
  strCliente: string;
  strCpfPortador: string;
  strAutorizacao: string;
  strNomeDocumento?: string; // Name of the document from required documents list
}

interface UploadDocumentFromBase64Props {
  atributos?: Record<string, string>;
  strTipoDocumento: string;
  strObservacoes: string;
  datDocumentoExterno?: string;
  strVisualizarWEB?: string;
  strCliente: string;
  strCpfPortador: string;
  strAutorizacao: string;
  contentType?: string;
}

export const useUploadProposalDocument = () => {
  const { markDocumentAsUploaded } = usePreProposalStore();

  const uploadProposalDocument = async (
    lngProposta: number,
    document: UploadProposalDocumentProps
  ) => {
    const uploadPayload = {
      atributos: document.atributos,
      strBase64: document.strBase64,
      strNomeArquivo: document.strNomeArquivo,
      strTipoDocumento: document.strTipoDocumento,
      strExtensao: document.strExtensao,
      strObservacoes: document.strObservacoes,
      strCliente: document.strCliente,
      strCpfPortador: document.strCpfPortador,
      strAutorizacao: document.strAutorizacao
    };

    console.log("[UPLOAD] PAYLOAD - lngProposta:", lngProposta.toString());
    console.log("[UPLOAD] PAYLOAD - fileName:", uploadPayload.strNomeArquivo);
    console.log("[UPLOAD] PAYLOAD - tipoDocumento:", uploadPayload.strTipoDocumento);
    console.log("[UPLOAD] PAYLOAD - extensao:", uploadPayload.strExtensao);
    console.log("[UPLOAD] PAYLOAD - cliente:", uploadPayload.strCliente);
    console.log("[UPLOAD] PAYLOAD - cpfPortador:", uploadPayload.strCpfPortador);
    console.log("[UPLOAD] PAYLOAD - autorizacao:", uploadPayload.strAutorizacao);
    console.log("[UPLOAD] PAYLOAD - base64Length:", uploadPayload.strBase64?.length || 0);
    console.log("[UPLOAD] PAYLOAD - payloadSize:", JSON.stringify(uploadPayload).length);

    try {
      const { data } = await proposalsResource.uploadDocument(
        lngProposta.toString(),
        uploadPayload
      );

      console.log("[UPLOAD] SUCCESS - Response:", data);
      return data;
    } catch (error) {
      console.error("[UPLOAD] ERROR - Message:", error instanceof Error ? error.message : String(error));
      console.error("[UPLOAD] ERROR - Status:", (error as any)?.response?.status);
      console.error("[UPLOAD] ERROR - StatusText:", (error as any)?.response?.statusText);
      console.error("[UPLOAD] ERROR - Response Data:", (error as any)?.response?.data);
      console.error("[UPLOAD] ERROR - Full Error:", error);
      throw error;
    }
  };

  const uploadDocumentFromUrl = async (
    lngProposta: number,
    documentUrl: string,
    documentData: UploadDocumentFromUrlProps
  ) => {
    console.log("[UPLOAD FROM URL] ENTRY");
    console.log("[UPLOAD FROM URL] lngProposta:", lngProposta);
    console.log("[UPLOAD FROM URL] documentUrl:", documentUrl);
    console.log("[UPLOAD FROM URL] strTipoDocumento:", documentData?.strTipoDocumento);

    try {
      // Validate inputs
      if (!lngProposta || lngProposta === 0) {
        const error = new Error("lngProposta is required and must be greater than 0");
        console.error("Validation error - lngProposta:", error);
        throw error;
      }

      if (!documentUrl) {
        const error = new Error("documentUrl is required");
        console.error("Validation error - documentUrl:", error);
        throw error;
      }

      if (!documentData?.strTipoDocumento) {
        const error = new Error("strTipoDocumento is required");
        console.error("Validation error - strTipoDocumento:", error);
        throw error;
      }

      // Download file from URL
      console.log("start upload - fetching from URL:", documentUrl);
      const response = await fetch(documentUrl);
      if (!response.ok) {
        throw new Error(`Failed to download file: ${response.statusText}`);
      }

      console.log("response ok")

      // Get file as blob
      const blob = await response.blob();

      // Get file extension ONLY from Content-Type (never from URL)
      const contentType = response.headers.get("content-type") || "";

      let fileExtension = "jpg"; // Default extension

      // Determine extension from Content-Type (most reliable and only source)
      if (contentType) {
        if (contentType.includes("application/pdf") || contentType.includes("pdf")) {
          fileExtension = "pdf";
        } else if (
          contentType.includes("image/jpeg") ||
          contentType.includes("image/jpg")
        ) {
          fileExtension = "jpg";
        } else if (contentType.includes("image/png")) {
          fileExtension = "png";
        } else if (contentType.includes("image/gif")) {
          fileExtension = "gif";
        } else if (contentType.includes("image/webp")) {
          fileExtension = "webp";
        }
      }

      console.log("[UPLOAD FROM URL] Detected extension:", fileExtension, "from Content-Type:", contentType);

      // Convert blob to base64
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const result = reader.result as string;
          // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
          const base64String = result.split(",")[1] || result;
          resolve(base64String);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      // Generate filename from document name or use default
      const maxFileNameLength = 60;
      const extensionWithDot = `.${fileExtension}`;
      const maxBaseLength = maxFileNameLength - extensionWithDot.length;

      let fileName: string;

      if (documentData.strNomeDocumento) {
        // Use document name from required documents list
        // Clean the name: remove special characters, limit length, add extension
        const cleanName = documentData.strNomeDocumento
          .replace(/[^a-zA-Z0-9\s-]/g, "") // Remove special chars except spaces and hyphens
          .replace(/\s+/g, "-") // Replace spaces with hyphens
          .toLowerCase()
          .substring(0, maxBaseLength); // Limit length

        fileName = `${cleanName}${extensionWithDot}`;
      } else {
        // Fallback: use timestamp if no document name provided
        const timestamp = Date.now();
        const baseFileName = `doc-${timestamp}`;
        fileName = `${baseFileName}${extensionWithDot}`;

        // Ensure filename doesn't exceed 60 characters
        if (fileName.length > maxFileNameLength) {
          const truncatedBase = baseFileName.substring(0, maxBaseLength);
          fileName = `${truncatedBase}${extensionWithDot}`;
        }
      }

      console.log("[UPLOAD FROM URL] Generated fileName:", fileName, "from document name:", documentData.strNomeDocumento);

      console.log("fileDownload", {
        atributos: documentData.atributos,
        strExtensao: fileExtension,
        strTipoDocumento: documentData.strTipoDocumento,
        strObservacoes: documentData.strObservacoes,
        strNomeArquivo: fileName,
        datDocumentoExterno:
          documentData.datDocumentoExterno || new Date().toISOString(),
        strVisualizarWEB: documentData.strVisualizarWEB || "S",
        strCliente: documentData.strCliente,
        strCpfPortador: documentData.strCpfPortador,
        strAutorizacao: documentData.strAutorizacao,
        lngProposta: lngProposta.toString()
      })

      // Prepare document data
      const uploadData: UploadProposalDocumentProps = {
        atributos: documentData.atributos,
        strExtensao: fileExtension,
        strTipoDocumento: documentData.strTipoDocumento,
        strObservacoes: documentData.strObservacoes,
        strBase64: base64,
        strNomeArquivo: fileName,
        datDocumentoExterno:
          documentData.datDocumentoExterno || new Date().toISOString(),
        strVisualizarWEB: documentData.strVisualizarWEB || "S",
        strCliente: documentData.strCliente,
        strCpfPortador: documentData.strCpfPortador,
        strAutorizacao: documentData.strAutorizacao,
        lngProposta: lngProposta.toString()
      };

      console.log("[UPLOAD FROM URL] READY TO UPLOAD");
      console.log("[UPLOAD FROM URL] fileName:", fileName);
      console.log("[UPLOAD FROM URL] fileExtension:", fileExtension);
      console.log("[UPLOAD FROM URL] base64Length:", base64.length);
      console.log("[UPLOAD FROM URL] strTipoDocumento:", uploadData.strTipoDocumento);
      console.log("[UPLOAD FROM URL] strCliente:", uploadData.strCliente);
      console.log("[UPLOAD FROM URL] strCpfPortador:", uploadData.strCpfPortador);

      // Upload document
      const result = await uploadProposalDocument(lngProposta, uploadData);

      // Mark document as uploaded after successful upload
      markDocumentAsUploaded(documentData.strTipoDocumento);

      console.log("[UPLOAD FROM URL] SUCCESS");
      return result;
    } catch (error) {
      console.error("[UPLOAD FROM URL] ERROR");
      console.error("[UPLOAD FROM URL] Error Message:", error instanceof Error ? error.message : String(error));
      console.error("[UPLOAD FROM URL] Error Status:", (error as any)?.response?.status);
      console.error("[UPLOAD FROM URL] Error Response:", (error as any)?.response?.data);
      throw error;
    }
  };

  const uploadDocumentFromBase64 = async (
    lngProposta: number,
    base64: string,
    documentData: UploadDocumentFromBase64Props
  ) => {
    try {
      // Determine file extension from content type or default to jpg
      let fileExtension = "jpg"; // Default extension
      const contentType = documentData.contentType || "";

      if (
        contentType.includes("image/jpeg") ||
        contentType.includes("image/jpg")
      ) {
        fileExtension = "jpg";
      } else if (contentType.includes("image/png")) {
        fileExtension = "png";
      } else if (contentType.includes("image/gif")) {
        fileExtension = "gif";
      } else if (contentType.includes("image/webp")) {
        fileExtension = "webp";
      } else if (contentType.includes("application/pdf") || contentType.includes("pdf")) {
        fileExtension = "pdf";
      }

      // Remove data URL prefix if present (e.g., "data:image/jpeg;base64,")
      const base64String = base64.includes(",") ? base64.split(",")[1] : base64;

      // Generate filename with timestamp - limit to 60 characters
      const timestamp = Date.now();
      const baseFileName = `selfie-${timestamp}`;
      const maxFileNameLength = 60;
      const extensionWithDot = `.${fileExtension}`;
      const maxBaseLength = maxFileNameLength - extensionWithDot.length;

      let fileName = `${baseFileName}${extensionWithDot}`;

      // Ensure filename doesn't exceed 60 characters
      if (fileName.length > maxFileNameLength) {
        const truncatedBase = baseFileName.substring(0, maxBaseLength);
        fileName = `${truncatedBase}${extensionWithDot}`;
      }

      // Prepare document data
      const uploadData: UploadProposalDocumentProps = {
        atributos: documentData.atributos,
        strExtensao: fileExtension,
        strTipoDocumento: documentData.strTipoDocumento,
        strObservacoes: documentData.strObservacoes,
        strBase64: base64String,
        strNomeArquivo: fileName,
        datDocumentoExterno:
          documentData.datDocumentoExterno || new Date().toISOString(),
        strVisualizarWEB: documentData.strVisualizarWEB || "S",
        strCliente: documentData.strCliente,
        strCpfPortador: documentData.strCpfPortador,
        strAutorizacao: documentData.strAutorizacao,
        lngProposta: lngProposta.toString()
      };

      console.log("=== uploadDocumentFromBase64 - READY TO UPLOAD ===", {
        lngProposta,
        fileName,
        fileExtension,
        base64Length: base64String.length,
        strTipoDocumento: uploadData.strTipoDocumento,
        strCliente: uploadData.strCliente,
        strCpfPortador: uploadData.strCpfPortador,
        strAutorizacao: uploadData.strAutorizacao
      });

      // Upload document
      const result = await uploadProposalDocument(lngProposta, uploadData);

      // Mark document as uploaded after successful upload
      markDocumentAsUploaded(documentData.strTipoDocumento);

      return result;
    } catch (error) {
      console.error("Error uploading document from base64:", error);
      throw error;
    }
  };

  return {
    uploadProposalDocument,
    uploadDocumentFromUrl,
    uploadDocumentFromBase64
  };
};
